import { useEffect, useMemo, useState } from 'react';

import { FormContent, FormRow, FormInputWrapper, BlockTitle } from '@/components/old';

import { DocumentTypeCode, DocumentTypeEntity } from '@/api/types';

import { StepsNavigationName } from '../../../../config/services';
import { getERZDocumentsSelect } from '../../../../modules/erz/helpers';
import InputDate from '../../../Inputs/InputDate';
import { FormConstants, HealthDataFieldName } from '../../constants';
import FileUpload from '../../FileUpload';
import InitialSelect from '../../Select';

import { HealthFormDataProps } from './types';

interface Props {
  formData: HealthFormDataProps;
  isStepMandatory?: boolean;
  handleInputChange: (name: string, value: unknown) => void;
  handleClearInput: (name: string) => void;
}

export const HealthData: React.FC<Props> = ({
  formData,
  isStepMandatory = false,
  handleInputChange,
  handleClearInput,
}) => {
  const [optionsPersonDoc, setOptionsPersonDoc] = useState<DocumentTypeEntity[]>([]);

  const healthDataDoc = useMemo(
    () => formData.healthDataDoc || isStepMandatory,
    [formData.healthDataDoc, isStepMandatory],
  );
  const healthDataDocType = useMemo(
    () => (formData.healthDataDocType ? JSON.parse(formData.healthDataDocType) : null),
    [formData.healthDataDocType],
  );
  const healthDataDocIssueDate = useMemo(
    () => (formData.healthDataDocIssueDate ? new Date(formData.healthDataDocIssueDate) : null),
    [formData.healthDataDocIssueDate],
  );
  const healthDataDocExpireDate = useMemo(
    () => (formData.healthDataDocExpireDate ? new Date(formData.healthDataDocExpireDate) : null),
    [formData.healthDataDocExpireDate],
  );

  useEffect(() => {
    if (healthDataDoc) {
      (async () => {
        if (!formData.healthDataDocType) {
          const [document] = await getERZDocumentsSelect({
            allowedDocumentTypeCodes: [DocumentTypeCode.MEDICAL_REFERENCE],
            setOptions: setOptionsPersonDoc,
          });
          handleInputChange(HealthDataFieldName.healthDataDocType, document);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthDataDocType, healthDataDoc]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    const booleanValue = value.toLowerCase() === 'true';

    if (!booleanValue) {
      [
        HealthDataFieldName.healthDataDocType,
        HealthDataFieldName.healthDataDocSerialNumber,
        HealthDataFieldName.healthDataDocIssuingAuthority,
        HealthDataFieldName.healthDataDocIssueDate,
        HealthDataFieldName.healthDataDocExpireDate,
        HealthDataFieldName.healthDataDocument,
      ].forEach(handleClearInput);
    }

    handleInputChange(name, booleanValue);
  };

  return (
    <>
      <BlockTitle id="step-description" paddingBottomMdExtra bgLight>
        {StepsNavigationName.health}
      </BlockTitle>

      <FormContent>
        {!isStepMandatory && (
          <FormRow>
            <FormInputWrapper
              label="Ви хочете додати дані документа?"
              labelClassName="form-input__label_margin-bottom-md"
              marginBottomNone
            >
              <fieldset id={HealthDataFieldName.healthDataDoc} className="form-input__radiobuttons">
                <label className="radio" htmlFor={HealthDataFieldName.healthDataDoc + '_yes'}>
                  <input
                    className="radio__input"
                    type="radio"
                    id={HealthDataFieldName.healthDataDoc + '_yes'}
                    name={HealthDataFieldName.healthDataDoc}
                    value="true"
                    checked={healthDataDoc}
                    onChange={handleRadioChange}
                  />
                  <span className="radio__checkmark"></span>
                  <span className="radio__label">Так</span>
                </label>

                <label className="radio" htmlFor={HealthDataFieldName.healthDataDoc + '_no'}>
                  <input
                    className="radio__input"
                    type="radio"
                    id={HealthDataFieldName.healthDataDoc + '_no'}
                    name={HealthDataFieldName.healthDataDoc}
                    value="false"
                    checked={!healthDataDoc}
                    onChange={handleRadioChange}
                  />
                  <span className="radio__checkmark"></span>
                  <span className="radio__label">Ні</span>
                </label>
              </fieldset>
            </FormInputWrapper>
          </FormRow>
        )}

        {(isStepMandatory || healthDataDoc) && (
          <>
            <FormInputWrapper
              label="Тип документа, що підтверджує стан здоров'я"
              required
              marginBottomNone
              className="no-group"
              data-groupName="healthData"
            >
              <div className="input-select js-input-select">
                <InitialSelect
                  name={HealthDataFieldName.healthDataDocType}
                  options={optionsPersonDoc}
                  placeholder="Не вибрано"
                  defaultValue={healthDataDocType}
                  onChange={(newValue, name) => handleInputChange(name, newValue)}
                  onMenuOpen={() => {
                    if (!optionsPersonDoc.length) {
                      getERZDocumentsSelect({
                        allowedDocumentTypeCodes: [DocumentTypeCode.MEDICAL_REFERENCE],
                        setOptions: setOptionsPersonDoc,
                      });
                    }
                  }}
                />
              </div>
            </FormInputWrapper>

            <FormInputWrapper label="Серія та номер" required marginBottomNone>
              <input
                className="form-input__input"
                placeholder="Не вказано"
                type="text"
                name={HealthDataFieldName.healthDataDocSerialNumber}
                value={formData.healthDataDocSerialNumber || ''}
                onChange={({ target: { name, value } }) => handleInputChange(name, value)}
                maxLength={FormConstants.INPUT_MAX_LENGTH}
              />
            </FormInputWrapper>

            <FormInputWrapper label="Орган видачі" required marginBottomNone>
              <input
                className="form-input__input"
                placeholder="Не вказано"
                type="text"
                name={HealthDataFieldName.healthDataDocIssuingAuthority}
                value={formData.healthDataDocIssuingAuthority || ''}
                onChange={({ target: { name, value } }) => handleInputChange(name, value)}
                maxLength={FormConstants.INPUT_MAX_LENGTH}
              />
            </FormInputWrapper>

            <FormRow>
              <InputDate
                required
                minDateBirth
                maxDateToday
                label="Дата видачі"
                name={HealthDataFieldName.healthDataDocIssueDate}
                value={healthDataDocIssueDate}
                onChange={(date): void => {
                  handleInputChange(HealthDataFieldName.healthDataDocIssueDate, date);
                }}
              />
              <InputDate
                required
                minDateToday
                label="Термін дії"
                name={HealthDataFieldName.healthDataDocExpireDate}
                value={healthDataDocExpireDate}
                onChange={(date): void => {
                  handleInputChange(HealthDataFieldName.healthDataDocExpireDate, date);
                }}
              />
            </FormRow>

            <FormInputWrapper label="Документ, що підтверджує стан здоров'я" required marginBottomNone>
              <FileUpload
                inputName={HealthDataFieldName.healthDataDocument}
                inputTitle="Додати сканкопію документа"
                value={formData.healthDataDocument ?? []}
                handleInputChange={handleInputChange}
                handleClearInput={handleClearInput}
              />
            </FormInputWrapper>
          </>
        )}
      </FormContent>
    </>
  );
};
